import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { formatDate } from "../../utils/helpers";
import { CALL_STATUS_OPTIONS, CALL_STATUS_OTHERS, isStandardCallStatus } from "../../utils/callStatusOptions";
import MobileNavbar from "../layout/MobileNavbar";
import MobileChatPanel from "./MobileChatPanel";
import RescheduleModal from "../modals/RescheduleModal";
import NapFieldsGroup, { napFieldsComplete, vacantPortsPayload } from "../common/NapFieldsGroup";
import ButtonSpinner from "../common/ButtonSpinner";
import FetchingOverlay from "../common/FetchingOverlay";
import { useConfirm } from "../../context/ConfirmContext";

const DISPLAY_FIELDS = [
  { key: "jo_number", label: "JO Number" },
  { key: "job_date", label: "Dispatch Date", format: formatDate },
  { key: "jo_status", label: "Status" },
  { key: "team_number", label: "Team Number" },
  { key: "personnel_names", label: "Personnel" },
  { key: "account_number", label: "Account Number" },
  { key: "reference_id", label: "Reference ID" },
  { key: "customer_full_name", label: "Customer Name" },
  { key: "customer_mobile_number", label: "Mobile Number" },
  { key: "customer_alt_mobile_number", label: "Alt Mobile" },
  { key: "team_leader_name", label: "Sales Agent" },
  { key: "complete_address", label: "Address" },
  { key: "municipality", label: "Municipality" },
  { key: "barangay", label: "Barangay" },
  { key: "plan", label: "Plan" },
  { key: "jo_status_remarks", label: "Status Remarks" },
  { key: "call_status", label: "Call Status", type: "call_status" },
  { key: "call_status_remarks", label: "Call Remarks" },
  { key: "serial_number", label: "Modem Serial Number" },
  { key: "record_id", label: "IPTV Serial Number" },
  { key: "time_in", label: "Time In" },
  { key: "time_out", label: "Time Out" },
];

// Same read-only set as desktop
const READ_ONLY = new Set(["jo_number", "job_date", "account_number", "reference_id", "customer_full_name"]);

const CANCELLATION_REASON_OPTIONS = [
  "Cancelled by Subs",
  "Needs RTA",
  "Full NAP",
  "No Converge Facility",
  "Other reason",
];

const STATUS_COLORS = {
  Pending: "bg-yellow-100 text-yellow-800",
  Completed: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-800",
};

export default function MobileJobOrderDetail({ jobOrderId, historyMode = false, onBack, onRefresh, onNapLocate = null, notifRef }) {
  const { canEditJO, canEditField, canViewChat, canPerformLifecycle, hasPerm } = useAuth();
  const { confirm } = useConfirm();
  const [jo, setJo] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);

  // Lifecycle action state
  const [confirmAction, setConfirmAction] = useState(null); // "complete" | "cancel" | null
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmRemark, setConfirmRemark] = useState("");
  const [confirmTeamNumber, setConfirmTeamNumber] = useState("");
  const [confirmPersonnelNames, setConfirmPersonnelNames] = useState("");
  const [confirmSerialNumber, setConfirmSerialNumber] = useState("");
  const [confirmRecordId, setConfirmRecordId] = useState("");
  const [confirmNapCode, setConfirmNapCode] = useState("");
  const [confirmNapId, setConfirmNapId] = useState("");
  const [confirmNapPort, setConfirmNapPort] = useState("");
  const [confirmVacantPorts, setConfirmVacantPorts] = useState("");
  const [confirmNapPhoto, setConfirmNapPhoto] = useState(null);
  const [confirmCancelReasons, setConfirmCancelReasons] = useState([]);

  // Update chat badge on detail header when SSE event arrives
  useEffect(() => {
    if (!notifRef) return;
    const interval = setInterval(() => {
      const ev = notifRef.current;
      if (ev && ev.job_order_id === jobOrderId && !chatOpen) {
        notifRef.current = null;
        setJo((prev) => prev ? { ...prev, chat_count: ev.chat_count, has_unread_chat: true, unread_count: (prev.unread_count || 0) + 1 } : prev);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [notifRef, jobOrderId, chatOpen]);

  // Keep the latest onBack in a ref so the fetch effect below need not list it
  // as a dependency. onBack is recreated on every parent render, and on the FST
  // mobile tree those happen ~1-2x/second from GPS watchPosition updates in
  // AuthContext (only on a secure HTTPS context like production, where the
  // browser allows geolocation — hence not reproducible on the plain-HTTP local
  // IP). Listing onBack here caused the detail to re-fetch and flash "Loading…"
  // on every GPS tick. Refetch only when the job order id actually changes.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/job-orders/${jobOrderId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setJo(data);
        setForm(data);
      })
      .catch(() => { if (!cancelled) onBackRef.current?.(); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobOrderId]);

  if (loading || !jo) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <MobileNavbar />
        <div className="flex-1 flex items-center justify-center text-gray-400">Loading…</div>
      </div>
    );
  }

  const isTerminal = jo.jo_status === "Completed" || jo.jo_status === "Cancelled" || jo.is_history || historyMode;

  // Permission-driven editability — same logic as desktop
  const canEditThisField = (key) =>
    !READ_ONLY.has(key) && canEditField(key) && canEditJO && !isTerminal;

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Save all editable fields — same as desktop saveFields
  const saveFields = async (overrides = {}) => {
    const body = {};
    DISPLAY_FIELDS.forEach(({ key }) => {
      if (!READ_ONLY.has(key) && key !== "jo_status") {
        body[key] = form[key] ?? null;
      }
    });
    // Include personnel IDs so backend can resolve display names correctly
    body.personnel_1_id = form.personnel_1_id ?? null;
    body.personnel_2_id = form.personnel_2_id ?? null;
    await api.put(`/job-orders/${jo.id}`, { ...body, ...overrides });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveFields();
      setMessage({ type: "success", text: "Saved successfully." });
      onRefresh();
      const { data } = await api.get(`/job-orders/${jobOrderId}`);
      setJo(data);
      setForm(data);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  // Lifecycle: Complete
  const handleComplete = async () => {
    // Keep the confirm dialog mounted (spinner showing) while the request — which
    // re-fetches NAP details from the external API — runs.
    setSaving(true);
    try {
      await saveFields({
        jo_status_remarks: confirmRemark,
        serial_number: confirmSerialNumber,
        record_id: confirmRecordId || null,
        nap_code: confirmNapCode,
        nap_id: confirmNapId,
        nap_port: confirmNapPort,
        vacant_ports: vacantPortsPayload(confirmVacantPorts),
        team_number: confirmTeamNumber,
        personnel_names: confirmPersonnelNames,
      });
      if (confirmNapPhoto) {
        const photoForm = new FormData();
        photoForm.append("file", confirmNapPhoto);
        await api.post(`/job-orders/${jo.id}/nap-photo`, photoForm);
      }
      const { data } = await api.post(`/job-orders/${jo.id}/complete`);
      setSaving(false);
      setConfirmAction(null);
      await confirm({
        title: "Job Order Completed",
        message: data?.nap_details_updated
          ? "Job order completed successfully and NAP details updated."
          : "Job order completed successfully.",
        confirmLabel: "OK",
        cancelLabel: "OK",
      });
      onRefresh();
      onBack();
    } catch (err) {
      setSaving(false);
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to complete." });
    }
  };

  // Lifecycle: Cancel
  const handleCancel = async (finalRemark, reasons, teamNumber, personnelNames) => {
    setSaving(true);
    setConfirmAction(null);
    try {
      await api.post(`/job-orders/${jo.id}/cancel`, {
        jo_status_remarks: finalRemark,
        cancellation_reasons: reasons,
        team_number: teamNumber || null,
        personnel_names: personnelNames || null,
      });
      setMessage({ type: "success", text: "Job Order cancelled." });
      onRefresh();
      onBack();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to cancel." });
    } finally {
      setSaving(false);
    }
  };

  // Lifecycle: Reschedule
  const handleRescheduleStart = async () => {
    setSaving(true);
    try {
      await saveFields();
      setShowReschedule(true);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save before rescheduling." });
    } finally {
      setSaving(false);
    }
  };

  const handleRescheduleComplete = (updatedJo) => {
    setShowReschedule(false);
    setJo(updatedJo);
    setForm(updatedJo);
    setMessage({ type: "success", text: "Job Order successfully rescheduled." });
    onRefresh();
  };

  const hasEdits = DISPLAY_FIELDS.some(
    ({ key }) => !READ_ONLY.has(key) && key !== "jo_status" && canEditField(key) && String(form[key] ?? "") !== String(jo[key] ?? "")
  );

  // Render a field input based on type
  const renderFieldInput = (key, type, label) => {
    if (type === "call_status") {
      const value = form[key] ?? "";
      const isCustom = value && !isStandardCallStatus(value);
      return (
        <div>
          <select
            value={isCustom ? CALL_STATUS_OTHERS : value}
            onChange={(e) => {
              const v = e.target.value;
              handleChange(key, v === CALL_STATUS_OTHERS ? "" : v);
            }}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">— Select —</option>
            {CALL_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
            <option value={CALL_STATUS_OTHERS}>{CALL_STATUS_OTHERS}</option>
          </select>
          {(isCustom || (form[key] === "" && value === "")) && form[key] !== undefined && (
            <input
              type="text"
              value={isCustom ? value : ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder="Enter custom call status…"
              className="w-full border rounded-lg px-3 py-2.5 text-sm mt-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>
      );
    }
    if (type === "number") {
      return (
        <input
          type="number"
          value={form[key] ?? ""}
          onChange={(e) => handleChange(key, e.target.value)}
          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }
    return (
      <input
        type="text"
        value={form[key] ?? ""}
        onChange={(e) => handleChange(key, e.target.value)}
        className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <MobileNavbar />

      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-[52px] z-10">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 active:bg-gray-200"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold text-gray-900 truncate">
            {jo.jo_number || "Job Order Detail"}
          </h1>
          <p className="text-xs text-gray-500 truncate">{jo.customer_full_name}</p>
        </div>
        {canViewChat && (
          <button
            onClick={() => setChatOpen(true)}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full active:scale-95 transition-transform ${
              jo.has_unread_chat
                ? "bg-red-100 text-red-700 ring-1 ring-red-300"
                : (jo.chat_count || 0) > 0
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-500"
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {jo.has_unread_chat
              ? (jo.unread_count || 0)
              : (jo.chat_count || 0) > 0 ? jo.chat_count : "Chat"
            }
          </button>
        )}
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${historyMode ? "bg-orange-100 text-orange-800" : (STATUS_COLORS[jo.jo_status] || "bg-gray-100 text-gray-700")}`}>
          {historyMode ? "Rescheduled" : jo.jo_status}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
        {/* Message */}
        {message && (
          <div className={`rounded-lg px-3 py-2 text-sm ${
            message.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
          }`}>
            {message.text}
          </div>
        )}

        {/* Editable fields section */}
        {canEditJO && !isTerminal && DISPLAY_FIELDS.some(({ key }) => canEditThisField(key)) && (
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <h2 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">Editable Fields</h2>
            <div className="space-y-3">
              {DISPLAY_FIELDS.filter(({ key }) => canEditThisField(key)).map(({ key, label, type }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                  {renderFieldInput(key, type, label)}
                </div>
              ))}
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !hasEdits}
              className="w-full mt-4 py-2.5 rounded-lg text-white font-medium text-sm bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}

        {/* Lifecycle action buttons */}
        {!isTerminal && canPerformLifecycle && (
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Actions</h2>
            <div className="space-y-2">
              {hasPerm("jo.complete") && (
                <button
                  onClick={() => {
                    setConfirmAction("complete");
                    setConfirmRemark(form.jo_status_remarks || "");
                    setConfirmSerialNumber(form.serial_number || "");
                    setConfirmRecordId(form.record_id || "");
                    setConfirmNapCode(form.nap_code || "");
                    setConfirmNapId(form.nap_id || "");
                    setConfirmNapPort(form.nap_port || "");
                    setConfirmVacantPorts(form.vacant_ports ?? "");
                    setConfirmTeamNumber(form.team_number || "");
                    setConfirmPersonnelNames(form.personnel_names || "");
                  }}
                  className="w-full py-2.5 rounded-lg text-white font-medium text-sm bg-green-600 active:bg-green-700"
                >
                  Complete Job Order
                </button>
              )}
              {hasPerm("jo.reschedule") && (
                <button
                  onClick={handleRescheduleStart}
                  disabled={saving}
                  className="w-full py-2.5 rounded-lg font-medium text-sm border border-yellow-400 text-yellow-700 active:bg-yellow-50 disabled:opacity-50"
                >
                  Reschedule Job Order
                </button>
              )}
              {hasPerm("jo.cancel") && (
                <button
                  onClick={() => {
                    setConfirmAction("cancel");
                    setConfirmRemark(form.jo_status_remarks || "");
                    setConfirmCancelReasons([]);
                    setConfirmTeamNumber(form.team_number || "");
                    setConfirmPersonnelNames(form.personnel_names || "");
                  }}
                  className="w-full py-2.5 rounded-lg font-medium text-sm border border-red-300 text-red-600 active:bg-red-50"
                >
                  Cancel Job Order
                </button>
              )}
            </div>
          </div>
        )}

        {/* Read-only fields */}
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Job Order Information</h2>
          <div className="space-y-2.5">
            {DISPLAY_FIELDS.filter(({ key }) => !canEditThisField(key)).map(({ key, label, format }) => {
              const val = jo[key];
              let display = format ? format(val) : (val != null && val !== "" ? String(val) : "—");

              return (
                <div key={key} className="flex items-start gap-2">
                  <span className="text-xs text-gray-400 w-28 flex-shrink-0 pt-0.5">{label}</span>
                  <span className="text-sm text-gray-900 flex-1">{display}</span>
                </div>
              );
            })}
            {jo.nap_code && (
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-400 w-28 flex-shrink-0 pt-0.5">NAP Code</span>
                {onNapLocate ? (
                  <button
                    type="button"
                    onClick={() => onNapLocate(jo.nap_code)}
                    title="View this NAP on the map"
                    className="text-sm font-semibold text-blue-600 flex-1 text-left inline-flex items-center gap-1"
                  >
                    <span className="underline decoration-dotted">{jo.nap_code}</span>
                    <svg className="w-4 h-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                ) : (
                  <span className="text-sm text-gray-900 flex-1">{jo.nap_code}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reschedule History */}
        {jo.history && jo.history.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Reschedule History ({jo.history.length})
            </h2>
            <div className="space-y-3">
              {jo.history
                .slice()
                .sort((a, b) => new Date(b.rescheduled_at) - new Date(a.rescheduled_at))
                .map((entry, idx) => (
                  <div key={entry.id} className="border rounded-lg p-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      Sched {jo.history.length - idx} &mdash; {new Date(entry.rescheduled_at).toLocaleDateString("en-PH")}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-400">Date</span>
                        <p className="text-gray-800">{formatDate(entry.job_date)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Status</span>
                        <p className="text-gray-800">{entry.jo_status || "—"}</p>
                      </div>
                      {entry.jo_status_remarks && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Remarks</span>
                          <p className="text-gray-800">{entry.jo_status_remarks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Chat panel */}
      {chatOpen && (
        <MobileChatPanel
          jobOrderId={jo.id}
          joNumber={jo.jo_number}
          customerName={jo.customer_full_name}
          onClose={() => setChatOpen(false)}
          onCountUpdate={(_, count) => {
            setJo((prev) => prev ? { ...prev, chat_count: count, has_unread_chat: false, unread_count: 0 } : prev);
          }}
          notifRef={notifRef}
        />
      )}

      {/* Reschedule Modal */}
      {showReschedule && (
        <RescheduleModal
          jobOrderId={jo.id}
          joNumber={jo.jo_number}
          currentJobDate={jo.job_date}
          initialRemark={form.jo_status_remarks || ""}
          initialTeamNumber={form.team_number || ""}
          initialPersonnelNames={form.personnel_names || ""}
          onClose={() => setShowReschedule(false)}
          onComplete={handleRescheduleComplete}
        />
      )}

      {/* Complete Job Order Dialog */}
      {confirmAction === "complete" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <FetchingOverlay show={saving} />
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold mb-2">Complete Job Order</h3>
            <p className="text-sm text-gray-600 mb-3">Mark this job order as Completed?</p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Team Number <span className="text-red-500">*</span></label>
            <input type="text" value={confirmTeamNumber} readOnly
              className="w-full border rounded px-3 py-2 text-sm mb-2 bg-gray-100 text-gray-700 cursor-not-allowed" placeholder="Team Number..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">Personnel <span className="text-red-500">*</span></label>
            <input type="text" value={confirmPersonnelNames} readOnly
              className="w-full border rounded px-3 py-2 text-sm mb-2 bg-gray-100 text-gray-700 cursor-not-allowed" placeholder="Personnel..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
            <textarea value={confirmRemark} onChange={(e) => setConfirmRemark(e.target.value)} rows={3}
              className="w-full border rounded px-3 py-2 text-sm mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Remarks..." />
            <NapFieldsGroup
              napCode={confirmNapCode} onNapCodeChange={setConfirmNapCode}
              napId={confirmNapId} onNapIdChange={setConfirmNapId}
              napPort={confirmNapPort} onNapPortChange={setConfirmNapPort}
              vacantPorts={confirmVacantPorts} onVacantPortsChange={setConfirmVacantPorts}
              showPhoto napPhoto={confirmNapPhoto} onNapPhotoChange={setConfirmNapPhoto}
              labelClassName="block text-xs font-medium text-gray-700 mb-1"
              fieldMargin="mb-2"
            />
            <label className="block text-xs font-medium text-gray-700 mb-1">Modem Serial Number <span className="text-red-500">*</span></label>
            <input type="text" value={confirmSerialNumber} onChange={(e) => setConfirmSerialNumber(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Modem Serial Number..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">IPTV Serial Number</label>
            <input type="text" value={confirmRecordId} onChange={(e) => setConfirmRecordId(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="IPTV Serial Number..." />
            <div className="flex gap-2">
              <button onClick={() => setConfirmAction(null)} disabled={saving} className="flex-1 py-2 text-sm border rounded active:bg-gray-100 disabled:opacity-50">Cancel</button>
              <button onClick={handleComplete}
                disabled={saving || !confirmRemark.trim() || !confirmSerialNumber.trim() || !confirmTeamNumber.trim() || !confirmPersonnelNames.trim() || !napFieldsComplete({ napCode: confirmNapCode, napId: confirmNapId, napPort: confirmNapPort, vacantPorts: confirmVacantPorts }) || !confirmNapPhoto}
                className="flex-1 py-2 text-sm text-white rounded bg-green-600 active:bg-green-700 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {saving && <ButtonSpinner />}
                {saving ? "Completing…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Job Order Dialog */}
      {confirmAction === "cancel" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold mb-2">Cancel Job Order</h3>
            <p className="text-sm text-gray-600 mb-3">Are you sure you want to cancel this job order?</p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Team Number</label>
            <input type="text" value={confirmTeamNumber} onChange={(e) => setConfirmTeamNumber(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Team Number..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">Personnel</label>
            <input type="text" value={confirmPersonnelNames} onChange={(e) => setConfirmPersonnelNames(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Personnel..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
            <textarea value={confirmRemark} onChange={(e) => setConfirmRemark(e.target.value)} rows={3}
              className="w-full border rounded px-3 py-2 text-sm mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Remarks..." />
            <label className="block text-xs font-medium text-gray-700 mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
            <div className="space-y-1.5 mb-3">
              {CANCELLATION_REASON_OPTIONS.map((reason) => (
                <label key={reason} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={confirmCancelReasons.includes(reason)}
                    onChange={() => setConfirmCancelReasons((prev) =>
                      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
                    )}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  {reason}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmAction(null)} className="flex-1 py-2 text-sm border rounded active:bg-gray-100">Go Back</button>
              <button onClick={() => {
                  const prefix = confirmCancelReasons.length > 0 ? confirmCancelReasons.join("; ") : "";
                  const text = confirmRemark.trim();
                  const finalRemark = prefix && text ? `${prefix} - ${text}` : text || "";
                  handleCancel(finalRemark, confirmCancelReasons, confirmTeamNumber, confirmPersonnelNames);
                }}
                disabled={!confirmRemark.trim() || confirmCancelReasons.length === 0}
                className="flex-1 py-2 text-sm text-white rounded bg-red-600 active:bg-red-700 disabled:opacity-50">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
